import axios from 'axios';

// Em producao, frontend e API usam o mesmo dominio. Em desenvolvimento,
// continuamos apontando para o servidor local do backend.
const API_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints
export const authAPI = {
  signUp: (email, password, name) =>
    api.post('/auth/signup', { email, password, name }),
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profile) => api.put('/auth/profile', profile),
  updatePassword: (currentPassword, newPassword) =>
    api.put('/auth/password', { currentPassword, newPassword })
};

// Services endpoints
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getById: (id) => api.get(`/services/${id}`),
  create: (serviceData) => api.post('/services', serviceData),
  duplicate: (id) => api.post(`/services/${id}/duplicate`),
  reorder: (serviceIds) => api.put('/services/reorder', { serviceIds }),
  update: (id, serviceData) => api.put(`/services/${id}`, serviceData),
  remove: (id) => api.delete(`/services/${id}`)
};

// Bookings endpoints
export const bookingsAPI = {
  create: (bookingData) => api.post('/bookings', bookingData),
  getPreferences: () => api.get('/bookings/preferences'),
  getQuote: (serviceId, dateTime, bookingId, optionQuantities) => api.get('/bookings/quote', {
    params: { serviceId, dateTime, bookingId, optionQuantities: JSON.stringify(optionQuantities || {}) }
  }),
  getAvailability: (serviceId, date, bookingId, optionQuantities) =>
    api.get('/bookings/availability', { params: { serviceId, date, bookingId, optionQuantities: JSON.stringify(optionQuantities || {}) } }),
  getAvailabilityCalendar: (serviceId, start, end, bookingId, optionQuantities) =>
    api.get('/bookings/availability-calendar', {
      params: { serviceId, start, end, bookingId, optionQuantities: JSON.stringify(optionQuantities || {}) }
    }),
  getMyBookings: () => api.get('/bookings/my-bookings'),
  getById: (bookingId) => api.get(`/bookings/${bookingId}`),
  update: (bookingId, bookingData) => api.put(`/bookings/${bookingId}`, bookingData),
  getAll: () => api.get('/bookings'),
  updateStatus: (bookingId, status, adminNotes, startTime, collaboratorId) =>
    api.patch(`/bookings/${bookingId}`, { status, adminNotes, startTime, collaboratorId }),
  removeAdmin: (bookingId) => api.delete(`/bookings/admin/${bookingId}`),
  cancel: (bookingId) => api.delete(`/bookings/${bookingId}`)
};

// Admin endpoints
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateUserRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, { role }),
  getUserAvailability: (userId) => api.get(`/admin/users/${userId}/availability`),
  getAvailableCollaborators: (bookingId, startTime) => api.get(`/admin/bookings/${bookingId}/available-collaborators`, { params: { startTime } }),
  getSettings: () => api.get('/admin/settings'),
  updateWorkingHours: (workingHours) =>
    api.put('/admin/settings/working-hours', { workingHours }),
  addAlertEmail: (email) =>
    api.post('/admin/settings/alert-email', { email }),
  removeAlertEmail: (emailId) =>
    api.delete(`/admin/settings/alert-email/${emailId}`),
  updateAvailability: (availableSlots) =>
    api.put('/admin/settings/availability', { availableSlots })
};

export const collaboratorAPI = {
  getAvailability: () => api.get('/collaborator/availability'),
  updateAvailability: (availableSlots) => api.put('/collaborator/availability', { availableSlots })
};

export const contactAPI = {
  send: (contactData) => api.post('/contact', contactData)
};

export default api;
